import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle, Lightbulb, Save, Plus, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Header from '../../components/ui/Header';
import productService from '../../services/productService';
import { generateProductContent, discoverAndSuggestProducts, saveManualProduct } from '../../services/aiReviewService';

/**
 * AIReviewGenerator Component
 * Admin interface for generating AI-powered reviews and affiliate links
 */
const AIReviewGenerator = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // ALL STATE HOOKS - declared at the top before any conditional logic
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [reviewCount, setReviewCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // AI Product Suggestions state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionCount, setSuggestionCount] = useState(5);

  // Manual Product Addition state
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualFormData, setManualFormData] = useState({
    name: '',
    brand: '',
    category: 'cordless_stick',
    currentPrice: '',
    originalPrice: '',
    description: '',
    imageUrl: '',
    imageAlt: '',
    badge: '',
    discount: '0',
  });
  const [manualLoading, setManualLoading] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin-login');
    }
  }, [user, authLoading, navigate]);

  // Load products on component mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const data = await productService?.getAllProducts();
        setProducts(data || []);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Failed to load products');
      } finally {
        setLoadingProducts(false);
      }
    };

    // Only load products if user is authenticated
    if (user && !authLoading) {
      loadProducts();
    }
  }, [user, authLoading]);

  // Show loading state during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!user) {
    return null;
  }

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const data = await productService?.getAllProducts();
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleGenerateSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      setError(null);
      setSuggestions([]);

      const result = await discoverAndSuggestProducts(suggestionCount, false);
      
      setSuggestions(result?.suggestions || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Error generating suggestions:', err);
      setError(err?.message || 'Failed to generate product suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleSaveSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      setError(null);

      const result = await discoverAndSuggestProducts(suggestionCount, true);
      
      // Reload products list
      await loadProducts();
      
      // Build success message
      let message = `Successfully added ${result?.totalSaved} product${result?.totalSaved !== 1 ? 's' : ''} to your database`;
      
      if (result?.totalDuplicates > 0) {
        message += `. ${result?.totalDuplicates} product${result?.totalDuplicates !== 1 ? 's were' : ' was'} skipped (already exists)`;
      }
      
      setResult({
        type: 'suggestions',
        message,
        savedProducts: result?.savedProducts,
        duplicates: result?.duplicates,
      });
      
      setSuggestions([]);
      setShowSuggestions(false);
    } catch (err) {
      console.error('Error saving suggestions:', err);
      setError(err?.message || 'Failed to save product suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleManualInputChange = (field, value) => {
    setManualFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveManualProduct = async () => {
    try {
      // Validate required fields
      if (!manualFormData?.name || !manualFormData?.brand || !manualFormData?.currentPrice || !manualFormData?.description) {
        setError('Please fill in all required fields (Name, Brand, Price, Description)');
        return;
      }

      setManualLoading(true);
      setError(null);

      const result = await saveManualProduct(manualFormData);
      
      // Reload products list
      await loadProducts();
      
      setResult({
        type: 'manual',
        message: `Successfully added "${result?.product?.name}" by ${result?.product?.brand} to your database`,
        product: result?.product,
      });
      
      // Reset form
      setManualFormData({
        name: '',
        brand: '',
        category: 'cordless_stick',
        currentPrice: '',
        originalPrice: '',
        description: '',
        imageUrl: '',
        imageAlt: '',
        badge: '',
        discount: '0',
      });
      setShowManualForm(false);
    } catch (err) {
      console.error('Error saving manual product:', err);
      setError(err?.message || 'Failed to save product');
    } finally {
      setManualLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedProduct) {
      setError('Please select a product');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const contentData = await generateProductContent(selectedProduct);
      
      setResult({
        ...contentData,
        message: contentData?.isNewReview 
          ? 'Product review and buying guide generated successfully!' :'Product review and buying guide updated successfully!'
      });
    } catch (err) {
      console.error('Error generating content:', err);
      setError(err?.message || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Manual Product Addition Section */}
        <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg shadow-sm border border-green-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Plus className="w-6 h-6 text-green-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Manual Product Addition
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Add products manually to your catalog with full control over details
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowManualForm(!showManualForm)}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              {showManualForm ? (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </>
              )}
            </Button>
          </div>

          {showManualForm && (
            <div className="mt-4 bg-white rounded-lg border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Product Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name <span className="text-error">*</span>
                  </label>
                  <Input
                    value={manualFormData?.name}
                    onChange={(e) => handleManualInputChange('name', e?.target?.value)}
                    placeholder="e.g., V15 Detect Absolute"
                    disabled={manualLoading}
                  />
                </div>

                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brand <span className="text-error">*</span>
                  </label>
                  <Input
                    value={manualFormData?.brand}
                    onChange={(e) => handleManualInputChange('brand', e?.target?.value)}
                    placeholder="e.g., Dyson"
                    disabled={manualLoading}
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-error">*</span>
                  </label>
                  <Select
                    value={manualFormData?.category}
                    onChange={(e) => handleManualInputChange('category', e?.target?.value)}
                    disabled={manualLoading}
                  >
                    <option value="cordless_stick">Cordless Stick</option>
                    <option value="upright">Upright</option>
                    <option value="canister">Canister</option>
                    <option value="robot">Robot</option>
                    <option value="handheld">Handheld</option>
                    <option value="wet_dry">Wet/Dry</option>
                  </Select>
                </div>

                {/* Badge */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Badge (Optional)
                  </label>
                  <Select
                    value={manualFormData?.badge}
                    onChange={(e) => handleManualInputChange('badge', e?.target?.value)}
                    disabled={manualLoading}
                  >
                    <option value="">None</option>
                    <option value="editors_choice">Editor's Choice</option>
                    <option value="best_value">Best Value</option>
                    <option value="premium_choice">Premium Choice</option>
                    <option value="top_rated">Top Rated</option>
                    <option value="new_arrival">New Arrival</option>
                  </Select>
                </div>

                {/* Current Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Price ($) <span className="text-error">*</span>
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={manualFormData?.currentPrice}
                    onChange={(e) => handleManualInputChange('currentPrice', e?.target?.value)}
                    placeholder="299.99"
                    disabled={manualLoading}
                  />
                </div>

                {/* Original Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Original Price ($) (Optional)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={manualFormData?.originalPrice}
                    onChange={(e) => handleManualInputChange('originalPrice', e?.target?.value)}
                    placeholder="399.99"
                    disabled={manualLoading}
                  />
                </div>

                {/* Discount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount (%)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={manualFormData?.discount}
                    onChange={(e) => handleManualInputChange('discount', e?.target?.value)}
                    disabled={manualLoading}
                  />
                </div>

                {/* Image URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL (Optional)
                  </label>
                  <Input
                    value={manualFormData?.imageUrl}
                    onChange={(e) => handleManualInputChange('imageUrl', e?.target?.value)}
                    placeholder="https://..."
                    disabled={manualLoading}
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-error">*</span>
                  </label>
                  <textarea
                    value={manualFormData?.description}
                    onChange={(e) => handleManualInputChange('description', e?.target?.value)}
                    placeholder="Detailed product description..."
                    rows={4}
                    disabled={manualLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                {/* Image Alt Text */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Alt Text (Optional)
                  </label>
                  <Input
                    value={manualFormData?.imageAlt}
                    onChange={(e) => handleManualInputChange('imageAlt', e?.target?.value)}
                    placeholder="Descriptive alt text for accessibility"
                    disabled={manualLoading}
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <Button
                  onClick={() => setShowManualForm(false)}
                  variant="outline"
                  disabled={manualLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveManualProduct}
                  disabled={manualLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {manualLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Product
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* AI Product Discovery Section */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow-sm border border-purple-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Lightbulb className="w-6 h-6 text-purple-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  AI Product Discovery
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Let AI suggest popular vacuum products based on market trends and consumer demand
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Suggestions
                </label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={suggestionCount}
                  onChange={(e) => setSuggestionCount(parseInt(e?.target?.value, 10) || 5)}
                  disabled={suggestionsLoading}
                  className="w-full"
                />
              </div>
              <Button
                onClick={handleGenerateSuggestions}
                disabled={suggestionsLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {suggestionsLoading ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Discovering...
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-5 h-5 mr-2" />
                    Get AI Suggestions
                  </>
                )}
              </Button>
            </div>

            {/* Suggestions Display */}
            {showSuggestions && suggestions?.length > 0 && (
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">
                    Suggested Products ({suggestions?.length})
                  </h3>
                  <Button
                    onClick={handleSaveSuggestions}
                    disabled={suggestionsLoading}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Add All to Database
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {suggestions?.map((product, index) => (
                    <div 
                      key={index} 
                      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{product?.name}</h4>
                          <p className="text-sm text-gray-600">{product?.brand}</p>
                        </div>
                        <div className="flex items-center gap-1 bg-purple-100 px-2 py-1 rounded">
                          <Sparkles className="w-3 h-3 text-purple-600" />
                          <span className="text-xs font-medium text-purple-700">
                            {product?.popularityScore}/10
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700">Category:</span>
                          <span className="text-gray-600">{product?.category}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-700">Price:</span>
                          <span className="text-gray-600">{product?.priceRange}</span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Target:</span>
                          <span className="text-gray-600 ml-2">{product?.targetAudience}</span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-3 line-clamp-3">
                        {product?.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-gray-900">
              AI Review Generator
            </h1>
          </div>

          <p className="text-gray-600 mb-8">
            Generate authentic product reviews and affiliate links using AI. 
            This tool creates diverse, realistic reviews from different customer perspectives 
            and provides monetizable affiliate shopping links.
          </p>

          <div className="space-y-6">
            {/* AI Review Generator Section */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow-sm border border-indigo-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-6 h-6 text-indigo-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">AI Product Review Generator</h1>
                  <p className="text-sm text-gray-600 mt-1">Generate comprehensive product reviews and buying guides with AI</p>
                </div>
              </div>

              {/* Product Selection */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Product
                  </label>
                  <Select
                    value={selectedProduct}
                    onChange={(value) => setSelectedProduct(value)}
                    disabled={loadingProducts || loading}
                    options={[
                      { value: '', label: loadingProducts ? 'Loading products...' : 'Choose a product' },
                      ...products?.map(product => ({
                        value: product?.id,
                        label: `${product?.brand} ${product?.name} - $${product?.currentPrice}`
                      }))
                    ]}
                  />
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={!selectedProduct || loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating Product Review...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Product Review & Buying Guide
                    </>
                  )}
                </Button>
              </div>

              {/* Success Result */}
              {result && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-green-900">{result?.message}</h3>
                      <div className="mt-2 space-y-1 text-sm text-green-800">
                        {result?.reviewGenerated && (
                          <p>✓ Professional product review generated</p>
                        )}
                        {result?.affiliateLinksGenerated > 0 && (
                          <p>✓ {result?.affiliateLinksGenerated} affiliate links created</p>
                        )}
                      </div>
                      {result?.review && (
                        <div className="mt-3 p-3 bg-white rounded border border-green-200">
                          <p className="font-medium text-gray-900">{result?.review?.title}</p>
                          <p className="text-sm text-gray-600 mt-1">{result?.review?.summary}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-red-900">Error</h3>
                      <p className="text-sm text-red-800 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Important Note */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> AI-generated reviews are marked as "verified" and flagged in the database. 
              Affiliate links help monetize your platform while providing value to users. 
              Ensure compliance with FTC guidelines by disclosing affiliate relationships.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIReviewGenerator;